import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, FileText } from "lucide-react";
import { BloodDrip } from "@/components/BloodDrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CharacterDossiers() {
  const [data, setData] = useState({ enabled: false, characters: [] });
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/characters`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!data.enabled || data.characters.length === 0) return null;

  return (
    <section id="dossiers" data-testid="character-dossiers" className="relative py-24 sm:py-32 newsprint-grain">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-marker text-crimson text-base sm:text-lg mb-3 -rotate-1 inline-block"
        >
          know your monsters
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-12"
        >
          THE <span className="text-bloodglow">DOSSIERS</span>
          <BloodDrip />
        </motion.h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.characters.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`dossier-card-${c.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              onClick={() => setActive(c)}
              className="group relative border-2 border-border bg-pulp text-left transition-all duration-200 hover:border-crimson hover:-translate-y-1"
            >
              <div className="relative overflow-hidden aspect-[3/4] bg-void">
                <img
                  src={c.image}
                  alt={`${c.name} dossier artwork`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 font-marker text-[11px] bg-blooddeep text-white px-2.5 py-0.5 -rotate-3 ink-shadow-sm">
                  CASE FILE
                </span>
                <span className="absolute bottom-3 right-3 flex items-center gap-1 font-courier text-[10px] uppercase tracking-widest bg-void/85 text-yell px-2 py-1 border border-yell/40">
                  <FileText size={11} /> Open Dossier
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-impact uppercase tracking-wide text-xl text-paper group-hover:text-crimson transition-colors">
                  {c.name}
                </h3>
                {c.alias && (
                  <p className="font-marker text-bloodglow text-sm mt-1 -rotate-1 inline-block">a.k.a. {c.alias}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 font-courier text-[10px] uppercase tracking-widest text-paper/50">
                  {c.kill_count != null && <span>Kills: {c.kill_count}</span>}
                  {c.weapon && <span>Weapon: {c.weapon}</span>}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="dossier-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-void/90 backdrop-blur-md overflow-y-auto p-4 sm:p-8"
            onClick={() => setActive(null)}
          >
            <div
              className="relative mx-auto max-w-3xl border-2 border-blooddeep bg-pulp my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="dossier-modal-close"
                onClick={() => setActive(null)}
                className="absolute top-3 right-3 z-10 p-2 bg-void/85 border border-paper/20 text-paper/70 hover:text-crimson hover:border-crimson transition-colors"
                aria-label="Close dossier"
              >
                <X size={20} />
              </button>
              <div className="grid sm:grid-cols-2">
                <div className="relative bg-void">
                  <img src={active.image} alt={`${active.name} artwork`} className="w-full h-full object-cover max-h-[70vh]" />
                  <span className="absolute top-3 left-3 font-marker text-[11px] bg-blooddeep text-white px-2.5 py-0.5 -rotate-3 ink-shadow-sm">
                    CONFIDENTIAL
                  </span>
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="font-display text-2xl sm:text-3xl uppercase text-paper">{active.name}</h3>
                  {active.alias && (
                    <p className="font-marker text-bloodglow text-base mt-2 -rotate-1 inline-block">a.k.a. {active.alias}</p>
                  )}
                  <dl className="mt-6 space-y-3">
                    {active.role && (
                      <div className="flex justify-between gap-4 border-b border-blooddeep/30 pb-2">
                        <dt className="font-courier text-[10px] uppercase tracking-widest text-paper/40">Role</dt>
                        <dd className="font-type text-sm text-paper text-right">{active.role}</dd>
                      </div>
                    )}
                    {active.kill_count != null && (
                      <div className="flex justify-between gap-4 border-b border-blooddeep/30 pb-2">
                        <dt className="font-courier text-[10px] uppercase tracking-widest text-paper/40">Kill Count</dt>
                        <dd className="font-display text-xl text-crimson crimson-glow">{active.kill_count}</dd>
                      </div>
                    )}
                    {active.weapon && (
                      <div className="flex justify-between gap-4 border-b border-blooddeep/30 pb-2">
                        <dt className="font-courier text-[10px] uppercase tracking-widest text-paper/40">Weapon of Choice</dt>
                        <dd className="font-type text-sm text-paper text-right">{active.weapon}</dd>
                      </div>
                    )}
                    {active.first_appearance && (
                      <div className="flex justify-between gap-4 border-b border-blooddeep/30 pb-2">
                        <dt className="font-courier text-[10px] uppercase tracking-widest text-paper/40">First Appearance</dt>
                        <dd className="font-type text-sm text-paper text-right">{active.first_appearance}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="font-type text-sm text-paper/70 leading-relaxed mt-6 whitespace-pre-line">{active.bio}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
